import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import api from './api';

interface AuthState {
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  orgId: string | null;
  login: (orgId: string | null, role?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isSuperAdmin: false,
  orgId: null,
  login: () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [checked, setChecked] = useState(false); // evita flash de login antes de verificar token

  // Verificar sesión existente al cargar la app (solo una vez)
  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (!token) {
      setChecked(true);
      return;
    }

    api.get('/auth/me')
      .then((res) => {
        const id = res.data?.organization?.id || null;
        const role = res.data?.user?.role || res.data?.role;
        const isAdmin = role === 'super_admin';

        // Actualizar org-id en localStorage si llegó del servidor
        if (id) localStorage.setItem('org-id', id);

        if (id || isAdmin) {
          setOrgId(id);
          setIsAuthenticated(true);
          setIsSuperAdmin(isAdmin);
        } else {
          // El token es válido pero el usuario no tiene org ni es super_admin
          // Intentar leer del localStorage como fallback
          const savedOrgId = localStorage.getItem('org-id');
          const savedRole = (() => {
            try { return JSON.parse(localStorage.getItem('user-data') || '{}')?.role; } catch { return null; }
          })();
          if (savedOrgId || savedRole === 'super_admin') {
            setOrgId(savedOrgId || null);
            setIsAuthenticated(true);
            setIsSuperAdmin(savedRole === 'super_admin');
          }
        }
      })
      .catch(() => {
        // /auth/me falló — usar datos guardados en localStorage como fallback
        const savedOrgId = localStorage.getItem('org-id');
        const savedRole = (() => {
          try { return JSON.parse(localStorage.getItem('user-data') || '{}')?.role; } catch { return null; }
        })();
        if (savedOrgId || savedRole === 'super_admin') {
          setOrgId(savedOrgId || null);
          setIsAuthenticated(true);
          setIsSuperAdmin(savedRole === 'super_admin');
        } else {
          // Token inválido o expirado — limpiar sesión
          localStorage.removeItem('auth-token');
          localStorage.removeItem('org-id');
          localStorage.removeItem('user-data');
        }
      })
      .finally(() => {
        setChecked(true);
      });
  }, []);

  const login = useCallback((id: string | null, role?: string) => {
    setOrgId(id);
    setIsAuthenticated(true);
    setIsSuperAdmin(role === 'super_admin');
    setChecked(true);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('org-id');
    localStorage.removeItem('user-data');
    setIsAuthenticated(false);
    setIsSuperAdmin(false);
    setOrgId(null);
  }, []);

  // No renderizar nada hasta que el check inicial termine (evita flash al /login)
  if (!checked) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-dark">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isSuperAdmin, orgId, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
